import { useCallback, useEffect, useRef, useState } from 'react';
import { Send, Plus, Trash2, Bot, Loader2, Paperclip, MessageSquare, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { aiAPI } from '@/lib/api';
import { streamAI } from '@/lib/aiStream';
import { MarkdownText } from './MarkdownText';
import { RecordPicker } from './RecordPicker';

const SUGGESTIONS = [
  'Give me today\'s CRM snapshot and what I should focus on',
  'Which of my leads are still new and need a first call?',
  'How many applications are at offer stage right now?',
  'Find the lead named Ali and summarise where they are',
];

const TOOL_LABELS = {
  search_records: 'Searching CRM records',
  get_record: 'Reading record details',
  crm_overview: 'Checking live CRM numbers',
  list_leads: 'Listing leads',
};

const SessionList = ({ sessions, activeId, onSelect, onNew, onDelete }) => (
  <aside className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-slate-700 lg:pr-4 pb-4 lg:pb-0">
    <Button data-testid="ai-chat-new-btn" onClick={onNew} className="w-full bg-teal-600 hover:bg-teal-700 text-white mb-3">
      <Plus className="w-4 h-4 mr-1" /> New conversation
    </Button>
    <div className="space-y-1 max-h-40 lg:max-h-[520px] overflow-y-auto" data-testid="ai-chat-session-list">
      {sessions.length === 0 && <p className="text-xs text-gray-500 px-2">No conversations yet.</p>}
      {sessions.map((s) => (
        <div
          key={s.id}
          data-testid={`ai-chat-session-${s.id}`}
          className={`group flex items-center gap-2 rounded-lg px-2 py-2 cursor-pointer transition-colors ${activeId === s.id ? 'bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200' : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
          onClick={() => onSelect(s.id)}
        >
          <MessageSquare className="w-4 h-4 shrink-0" />
          <span className="text-sm truncate flex-1">{s.title}</span>
          <button
            type="button"
            data-testid={`ai-chat-session-delete-${s.id}`}
            onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
            className="opacity-60 lg:opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        </div>
      ))}
    </div>
  </aside>
);

const MessageBubble = ({ m, idx }) => (
  <div className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`} data-testid={`ai-chat-message-${idx}`}>
    {m.role === 'assistant' && (
      <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-white" /></div>
    )}
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'bg-teal-600 text-white' : 'bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600'}`}>
      {m.role === 'user'
        ? <p className="text-sm whitespace-pre-wrap">{m.content}</p>
        : <MarkdownText>{m.content}</MarkdownText>}
    </div>
  </div>
);

export const AIChatPanel = ({ disabled }) => {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [record, setRecord] = useState(null);
  const [showAttach, setShowAttach] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toolStatus, setToolStatus] = useState('');
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try { setSessions((await aiAPI.getSessions()).data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, toolStatus]);

  const openSession = async (id) => {
    if (busy) return;
    try {
      const { data } = await aiAPI.getSessionMessages(id);
      setSessionId(id);
      setMessages(data.messages);
    } catch { toast.error('Could not load conversation'); }
  };

  const newSession = () => { if (!busy) { setSessionId(null); setMessages([]); setRecord(null); } };

  const deleteSession = async (id) => {
    try {
      await aiAPI.deleteSession(id);
      if (id === sessionId) newSession();
      loadSessions();
    } catch { toast.error('Could not delete conversation'); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    setBusy(true);
    setMessages((prev) => [...prev, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    const appendDelta = (c) => setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + c };
      return next;
    });
    try {
      await streamAI('/ai/chat', {
        message: msg, session_id: sessionId,
        record_type: record?.record_type, record_id: record?.id,
      }, (ev) => {
        if (ev.type === 'session') setSessionId(ev.session_id);
        else if (ev.type === 'tool') setToolStatus(TOOL_LABELS[ev.name] || 'Looking things up');
        else if (ev.type === 'delta') { setToolStatus(''); appendDelta(ev.content); }
        else if (ev.type === 'error') toast.error(ev.message);
      });
    } catch (e) {
      toast.error(e.message);
    }
    setMessages((prev) => prev.filter((m, i) => !(i === prev.length - 1 && m.role === 'assistant' && !m.content)));
    setToolStatus('');
    setRecord(null);
    setShowAttach(false);
    setBusy(false);
    loadSessions();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="ai-chat-panel">
      <SessionList sessions={sessions} activeId={sessionId} onSelect={openSession} onNew={newSession} onDelete={deleteSession} />
      <section className="flex-1 min-w-0 flex flex-col">
        <div ref={scrollRef} className="flex-1 min-h-[360px] max-h-[520px] overflow-y-auto space-y-4 pr-1" data-testid="ai-chat-messages">
          {messages.length === 0 && (
            <div className="py-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Ask anything about your leads, students and applications. Claude checks live CRM data before answering.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    disabled={disabled || busy}
                    data-testid={`ai-chat-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-left text-sm rounded-xl border border-gray-200 dark:border-slate-600 px-3 py-2.5 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-slate-700 transition-colors text-gray-700 dark:text-gray-200 disabled:opacity-50"
                  >
                    <Search className="w-3.5 h-3.5 inline mr-1.5 text-teal-600" />{s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => <MessageBubble key={i} m={m} idx={i} />)}
          {busy && (toolStatus || !messages[messages.length - 1]?.content) && (
            <div className="flex items-center gap-2 text-xs text-teal-700 dark:text-teal-300 pl-11" data-testid="ai-chat-thinking">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {toolStatus || 'Claude is thinking…'}
            </div>
          )}
        </div>
        <div className="mt-4 space-y-2">
          {(showAttach || record) && <RecordPicker value={record} onChange={setRecord} testId="ai-chat-record" dropUp />}
          <div className="flex gap-2 items-end">
            <Button type="button" variant="outline" size="icon" data-testid="ai-chat-attach-btn" title="Attach a CRM record" onClick={() => setShowAttach((v) => !v)}>
              <Paperclip className="w-4 h-4" />
            </Button>
            <Textarea
              data-testid="ai-chat-input"
              value={input}
              disabled={disabled}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={disabled ? 'Add your Anthropic key to start' : 'Ask Claude… (Enter to send, Shift+Enter for new line)'}
              rows={2}
              className="resize-none"
            />
            <Button data-testid="ai-chat-send-btn" onClick={() => send()} disabled={disabled || busy || !input.trim()} className="bg-teal-600 hover:bg-teal-700 text-white h-[60px] px-4">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
